import { api } from "./api";

export type PushSyncState = {
  supported: boolean;
  permission: NotificationPermission | "unsupported";
  serviceWorkerActive: boolean;
  subscribed: boolean;
};

const unsupportedState: PushSyncState = {
  supported: false,
  permission: "unsupported",
  serviceWorkerActive: false,
  subscribed: false,
};

export function supportsWebPush(): boolean {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export function pushPermissionHelp(): string {
  const agent = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(agent)) {
    return "Bildirim izni kapalı. Ayarlar → Bildirimler → Yapısal İzin bölümünden açabilirsiniz.";
  }
  if (/Android/i.test(agent)) {
    return "Bildirim izni kapalı. Android uygulama/site bildirim ayarlarından açabilirsiniz.";
  }
  return "Bildirim izni tarayıcı veya işletim sistemi ayarlarından açılmalıdır.";
}

export function pushSetupHelp(): string {
  const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return ios ? 'iPhone/iPad: Siteyi Paylaş → Ana Ekrana Ekle ile kurun; ana ekran simgesinden açıp Bildirimleri aç düğmesine dokunun.'
    : 'Bildirimleri destekleyen bir tarayıcı kullanın; site iznini ve cihazın tarayıcı bildirim ayarlarını kontrol edin.';
}

function withTimeout<T>(promise: Promise<T>, milliseconds: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), milliseconds);
    promise.then(resolve, reject).finally(() => window.clearTimeout(timer));
  });
}

let subscriptionSync: Promise<PushSubscription | null> | null = null;

async function ensureSubscription(registration: ServiceWorkerRegistration, key: string | null | undefined) {
  if (subscriptionSync) return subscriptionSync;
  subscriptionSync = (async () => {
    let subscription = await registration.pushManager.getSubscription();
    const expectedKey = key ? base64ToUint8Array(key) : null;
    const actualKey = subscription?.options?.applicationServerKey;
    const keyChanged = expectedKey && actualKey && (actualKey.byteLength !== expectedKey.length
      || new Uint8Array(actualKey).some((byte, index) => byte !== expectedKey[index]));
    if (subscription && (keyChanged || (subscription.expirationTime && subscription.expirationTime <= Date.now()))) {
      if (!await subscription.unsubscribe()) throw new Error('Eski bildirim aboneliği yenilenemedi. Yeniden deneyin.');
      subscription = null;
    }
    if (!subscription && expectedKey) subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true, applicationServerKey: expectedKey as BufferSource,
    });
    return subscription;
  })();
  try { return await subscriptionSync; } finally { subscriptionSync = null; }
}

function base64ToUint8Array(value: string): Uint8Array {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const binary = atob(
    (value + padding).replaceAll("-", "+").replaceAll("_", "/"),
  );
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

export async function syncPushSubscription(
  vapidPublicKey: string | null | undefined,
  options: { requestPermission?: boolean } = {},
): Promise<PushSyncState> {
  if (!supportsWebPush()) return unsupportedState;

  let permission = Notification.permission;
  if (options.requestPermission && permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") {
    return {
      supported: true,
      permission,
      serviceWorkerActive: false,
      subscribed: false,
    };
  }

  const registration = await withTimeout(navigator.serviceWorker.ready, 12000, 'Bildirim servisi hazır değil. Sayfayı yenileyip tekrar deneyin.');
  const serviceWorkerActive = Boolean(registration.active);
  if (!serviceWorkerActive) {
    return { supported: true, permission, serviceWorkerActive, subscribed: false };
  }

  const subscription = await withTimeout(ensureSubscription(registration, vapidPublicKey), 15000, 'Cihazın bildirim aboneliği yanıt vermedi. Bağlantınızı kontrol edip tekrar deneyin.');
  if (!subscription) {
    return { supported: true, permission, serviceWorkerActive, subscribed: false };
  }

  await api("/api/push/subscribe", {
    method: "POST",
    body: subscription.toJSON(),
  });
  return { supported: true, permission, serviceWorkerActive, subscribed: true };
}
